import {
  CloudArrowUpIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import React, { useCallback, useRef, useState, useEffect } from "react";
import { useApiSnackbar } from "../../../../../../common/service/resources/mutators";
import { Callout, CalloutVariant } from "../../../../Callout";
import { Button } from "@truevault/aquilo";
import JSZip from "jszip";

interface StickyFileDropperProps {
  onFileUpload: (file: File) => Promise<void>;
  uploadedFiles: Array<{
    id: string;
    filename: string;
    size: number;
    status?: string;
    uploadedAt?: string;
    url?: string;
  }>;
  onFileDelete?: (fileId: string) => Promise<void>;
  disabled?: boolean;
  customIdentifierRowCount?: number;
}

// Maximum file size in bytes (15MB)
const MAX_FILE_SIZE = 15000000;

// Allowed file extensions for extracted files from ZIP
const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".txt",
  ".csv",
  ".xlsx",
  ".xls",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".json",
  ".mp4",
];

/**
 * Checks if a file has an allowed extension
 */
const isAllowedFileType = (filename: string): boolean => {
  const lowerFilename = filename.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => lowerFilename.endsWith(ext));
};

interface ZipExtractionResult {
  files: File[];
  skippedFiles: string[];
}

/**
 * Extracts files from a ZIP archive
 * @returns Object containing extracted File objects and list of skipped files with reasons
 */
const extractZipFiles = async (zipFile: File): Promise<ZipExtractionResult> => {
  const zip = await JSZip.loadAsync(zipFile);
  const skippedFiles: string[] = [];

  const filePromises = Object.entries(zip.files).map(async ([relativePath, zipEntry]) => {
    // Skip directories and hidden files (starting with .)
    if (zipEntry.dir || relativePath.startsWith("__MACOSX/") || relativePath.includes("/._")) {
      return null;
    }

    // Get the filename from the path
    const filename = relativePath.split("/").pop() || relativePath;

    // Skip hidden files
    if (filename.startsWith(".")) {
      return null;
    }

    // Check for allowed file types
    if (!isAllowedFileType(filename)) {
      skippedFiles.push(`${filename} (unsupported type)`);
      return null;
    }

    try {
      const blob = await zipEntry.async("blob");

      // Skip files that are too large
      if (blob.size > MAX_FILE_SIZE) {
        skippedFiles.push(`${filename} (exceeds 15MB)`);
        return null;
      }

      // Determine MIME type based on extension
      const ext = filename.toLowerCase().split(".").pop();
      const mimeTypes: Record<string, string> = {
        pdf: "application/pdf",
        doc: "application/msword",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        txt: "text/plain",
        csv: "text/csv",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        xls: "application/vnd.ms-excel",
        png: "image/png",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        gif: "image/gif",
        json: "application/json",
        mp4: "video/mp4",
      };

      const mimeType = mimeTypes[ext || ""] || "application/octet-stream";
      return new File([blob], filename, { type: mimeType });
    } catch (error) {
      console.error(`Error extracting file ${relativePath} from ZIP:`, error);
      skippedFiles.push(`${filename} (failed to extract)`);
      return null;
    }
  });

  const results = await Promise.all(filePromises);
  return {
    files: results.filter((file): file is File => file !== null),
    skippedFiles,
  };
};

export const StickyFileDropper = ({
  onFileUpload,
  uploadedFiles,
  onFileDelete,
  disabled = false,
  customIdentifierRowCount = 0,
}: StickyFileDropperProps) => {
  const [dragOver, setDragOver] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerLeft, setContainerLeft] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { apiSuccess, apiError } = useApiSnackbar();

  // Measure parent container width and position for sticky positioning
  useEffect(() => {
    if (!containerRef.current) return;

    const updatePosition = () => {
      const container = containerRef.current;
      if (container) {
        // Get the parent flex container (two levels up)
        const flexParent = container.parentElement?.parentElement;
        if (flexParent) {
          const rect = flexParent.getBoundingClientRect();
          setContainerWidth(rect.width);
          setContainerLeft(rect.left);
        }
      }
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, []);

  // Detect sticky state using IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When sentinel is NOT visible, we're sticky
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: [1],
        rootMargin: "-1px 0px 0px 0px",
      },
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  }, []);

  /**
   * Processes files for upload, handling ZIP extraction and bulk uploads
   */
  const processFilesForUpload = useCallback(
    async (files: File[]) => {
      if (disabled || uploading || files.length === 0) return;

      setUploading(true);
      const filesToUpload: File[] = [];
      const skippedFiles: string[] = [];
      let zipExtracted = false;

      // First, process all files and extract ZIPs
      for (const file of files) {
        // Check if it's a ZIP file
        if (
          file.type === "application/zip" ||
          file.type === "application/x-zip-compressed" ||
          file.name.toLowerCase().endsWith(".zip")
        ) {
          try {
            const result = await extractZipFiles(file);
            if (result.files.length > 0) {
              filesToUpload.push(...result.files);
              zipExtracted = true;
            }
            // Add any skipped files from within the ZIP
            if (result.skippedFiles.length > 0) {
              skippedFiles.push(...result.skippedFiles);
            }
            // If no valid files were found and nothing was skipped, the ZIP was empty
            if (result.files.length === 0 && result.skippedFiles.length === 0) {
              skippedFiles.push(`${file.name} (no valid files found)`);
            }
          } catch (error) {
            console.error(`Error extracting ZIP file ${file.name}:`, error);
            skippedFiles.push(`${file.name} (failed to extract)`);
          }
        } else if (file.size > MAX_FILE_SIZE) {
          skippedFiles.push(`${file.name} (exceeds 15MB)`);
        } else if (!isAllowedFileType(file.name)) {
          skippedFiles.push(`${file.name} (unsupported type)`);
        } else {
          filesToUpload.push(file);
        }
      }

      if (filesToUpload.length === 0) {
        setUploading(false);
        if (skippedFiles.length > 0) {
          apiError(`No valid files to upload. Skipped: ${skippedFiles.join(", ")}`);
        }
        return;
      }

      // Upload all files
      let successCount = 0;
      let failCount = 0;
      setUploadProgress({ current: 0, total: filesToUpload.length });

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        setUploadProgress({ current: i + 1, total: filesToUpload.length });
        try {
          await onFileUpload(file);
          successCount++;
        } catch (error) {
          console.error(`Failed to upload file ${file.name}:`, error);
          failCount++;
        }
      }

      setUploadProgress(null);
      setUploading(false);

      // Show appropriate success/error messages
      if (successCount > 0) {
        if (zipExtracted) {
          apiSuccess(
            `Successfully uploaded ${successCount} file${successCount !== 1 ? "s" : ""} from ZIP`,
          );
        } else if (successCount === 1) {
          apiSuccess("Your file has been successfully uploaded");
        } else {
          apiSuccess(`Successfully uploaded ${successCount} files`);
        }
      }

      if (failCount > 0) {
        apiError(`Failed to upload ${failCount} file${failCount !== 1 ? "s" : ""}`);
      }

      if (skippedFiles.length > 0) {
        // Show error for skipped files
        apiError(`Some files could not be uploaded: ${skippedFiles.join(", ")}`);
      }
    },
    [disabled, uploading, onFileUpload, apiSuccess, apiError],
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent) => {
      event.preventDefault();
      setDragOver(false);

      const files = Array.from(event.dataTransfer.files);
      await processFilesForUpload(files);
    },
    [processFilesForUpload],
  );

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (fileList && fileList.length > 0) {
        const files = Array.from(fileList);
        await processFilesForUpload(files);
      }

      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [processFilesForUpload],
  );

  const handleClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleDownload = async (attachment: any) => {
    try {
      const response = await fetch(attachment.url, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create a link element for download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", attachment.filename);

      // Append to body, click, and cleanup
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
      apiError("Failed to download file");
    }
  };

  const handleDelete = async (fileId: string) => {
    if (!onFileDelete || disabled) return;

    setIsDeleting(true);
    try {
      await onFileDelete(fileId);
      apiSuccess("File deleted successfully");
    } catch (error) {
      console.error("Error deleting file:", error);
      apiError("Failed to delete file");
    } finally {
      setIsDeleting(false);
    }
  };

  const uploadedOnlyFiles = uploadedFiles.filter((f) => f.status === "UPLOADED");

  // Additional height needed for custom identifier rows (69px per row)
  const additionalHeight = customIdentifierRowCount * 69;

  // Calculate dropper height based on number of files and custom identifier rows
  // Base: 540px with no files, 540 - 158 for 1 file, 540 - 27 - 99*2 for 2+ files
  // Add 69px per custom identifier row
  const calculateDropperHeight = () => {
    const baseHeight = 540 + additionalHeight;
    if (uploadedOnlyFiles.length === 0) return `${baseHeight}px`;
    if (uploadedOnlyFiles.length === 1) return `${baseHeight - 158}px`;
    return `${baseHeight - 27 - 99 * 2}px`;
  };

  return (
    <div
      ref={containerRef}
      className="flex flex-col flex-1 min-h-0"
      style={isSticky ? {} : { height: `${570 + additionalHeight}px` }}
    >
      {/* Sentinel element for sticky detection - positioned at top of component */}
      <div
        ref={sentinelRef}
        style={{
          height: "1px",
          width: "100%",
          marginTop: "-1px",
          pointerEvents: "none",
        }}
      />

      {/* Sticky file dropper - changes size based on sticky state */}
      <div
        className={isSticky ? "flex flex-col" : "flex-1 flex flex-col min-h-0"}
        style={
          isSticky && containerWidth > 0
            ? {
                position: "fixed",
                top: "0px",
                left: `${containerLeft}px`,
                width: `${containerWidth}px`,
                backgroundColor: "white",
                zIndex: 9999,
              }
            : {
                position: "sticky",
                top: "0px",
                backgroundColor: "white",
                marginTop: "-15px",
              }
        }
      >
        <Callout
          variant={CalloutVariant.WhiteDropShadow}
          className={isSticky ? "" : "flex-1 flex flex-col min-h-0"}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={handleFileSelect}
            disabled={disabled || uploading}
            accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.json,.mp4,.zip"
          />
          <div
            className={isSticky ? "tw-boundary" : "tw-boundary flex flex-col"}
            style={isSticky ? {} : { height: calculateDropperHeight(), flexShrink: 0 }}
          >
            {/* File dropper - compact when sticky, full when not */}
            <div
              className={`border-2 border-dashed rounded-lg text-center ${
                isSticky
                  ? "flex items-center justify-center p-3"
                  : "flex-1 flex flex-col items-center justify-center p-6"
              } ${
                dragOver
                  ? "border-blue-500 bg-blue-50"
                  : disabled || uploading
                  ? "border-gray-200 bg-gray-50"
                  : "border-gray-300 bg-white hover:border-gray-400 cursor-pointer"
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleClick}
            >
              {isSticky ? (
                /* Compact view when sticky */
                <div className="flex items-center justify-center space-x-2">
                  <CloudArrowUpIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-900">
                    {uploading
                      ? uploadProgress
                        ? `Uploading ${uploadProgress.current} of ${uploadProgress.total}...`
                        : "Uploading..."
                      : "Drop files or click to upload"}
                  </span>
                </div>
              ) : (
                /* Full view when not sticky */
                <div className="flex flex-col items-center justify-center">
                  <div className="flex items-center space-x-2">
                    <CloudArrowUpIcon className="h-12 w-12 text-gray-400" />
                    <ArchiveBoxIcon className="h-10 w-10 text-gray-300" />
                  </div>
                  <div className="mt-4">
                    <p className="text-lg font-medium text-gray-900">
                      {uploading
                        ? uploadProgress
                          ? `Uploading ${uploadProgress.current} of ${uploadProgress.total} files...`
                          : "Processing..."
                        : "Drop files here or click to upload"}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      Supports multiple files and ZIP archives
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      PDF, DOC, TXT, CSV, XLS, XLSX, PNG, JPG, GIF, JSON, MP4, ZIP (max 15MB per
                      file)
                    </p>
                    <p className="text-xs text-gray-400">Files are stored for 120 days</p>
                  </div>
                </div>
              )}
            </div>

            {/* File count - only show when sticky */}
            {isSticky && uploadedOnlyFiles.length > 0 && (
              <div className="mt-2 text-center">
                <span className="text-sm text-gray-600">
                  {uploadedOnlyFiles.length} file{uploadedOnlyFiles.length !== 1 ? "s" : ""}{" "}
                  uploaded
                </span>
              </div>
            )}
          </div>

          {/* File list - inside Callout when not sticky */}
          {!isSticky && uploadedOnlyFiles.length > 0 && (
            <div
              style={{
                overflowY: "auto",
                flex: "1 1 0",
                minHeight: 0,
                maxHeight: `${27 + 99 * 2}px`, // Header (27px) + 2 files (99px each)
                padding: "16px",
                borderTop: "1px solid #e5e7eb",
              }}
            >
              <div className="tw-boundary">
                <div className="pb-2 z-10">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-base font-medium text-gray-900">File(s) Uploaded</h3>
                    <span className="text-xs text-gray-500">Actions</span>
                  </div>
                </div>
                <div className="divide-y divide-gray-200">
                  {uploadedOnlyFiles.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="py-3 flex items-center justify-between hover:bg-gray-50"
                    >
                      <div className="flex items-center space-x-3">
                        <DocumentIcon className="h-8 w-8 text-gray-400" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{attachment.filename}</p>
                          <p className="text-xs text-gray-500">
                            {formatFileSize(attachment.size)}
                            {attachment.uploadedAt && (
                              <>
                                {" "}
                                • Uploaded {new Date(attachment.uploadedAt).toLocaleDateString()}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {attachment.url && (
                          <Button
                            color="tertiary"
                            size="m"
                            width="auto"
                            variant="round"
                            isCircular={true}
                            onClick={() => handleDownload(attachment)}
                            aria-label={`Download ${attachment.filename || "file"}`}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                          </Button>
                        )}
                        {onFileDelete && (
                          <Button
                            color="tertiary"
                            size="m"
                            width="auto"
                            variant="round"
                            isCircular={true}
                            onClick={() => handleDelete(attachment.id)}
                            disabled={isDeleting || disabled}
                            aria-label={`Delete ${attachment.filename || "file"}`}
                          >
                            <TrashIcon className="h-4 w-4 mr-1.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Callout>
      </div>
    </div>
  );
};
